// Обёртки над нашим API. apiBase приходит из index.js (вычислен от src скрипта).

// Таймаут quote «Найти» (T24): в бою 0,1–1,5 с, при гонке броней до 9 с. Дольше — запрос застрял
// на стороне гостя (обрыв/смена сети, сон вкладки): ждать сброса сокета ОС (30–60 с) не стоит
export const QUOTE_TIMEOUT_MS = 12_000;
// Таймаут брони (T34): обычно сервер отвечает за 3–4 с, в худшем случае (Шелтер тормозит, впереди
// очередь броней) — до полутора минут. 60 с тишины — скорее обрыв у гостя; повтор безопасен:
// у попытки свой ключ, сервер отдаст ответ первой (повтор делает lib/checkout.js)
export const BOOK_TIMEOUT_MS = 60_000;

// Ошибка связи (fetch упал или наш таймаут) — такую можно повторить; ответы сервера кода не несут
export const isConnectionError = (err) => err?.code === "network" || err?.code === "timeout";

const NO_CONNECTION = "Нет связи с сервером бронирования. Проверьте интернет и обновите страницу.";

// fetchImpl по умолчанию ленивый: берёт globalThis.fetch при каждом вызове — подмена после
// загрузки страницы (ручные проверки) работает. Зовём только как локальную функцию: нативный
// fetch с чужим this бросает Illegal invocation
export function createApi(
  apiBase,
  {
    fetchImpl = (...a) => globalThis.fetch(...a),
    quoteTimeoutMs = QUOTE_TIMEOUT_MS,
    bookTimeoutMs = BOOK_TIMEOUT_MS,
  } = {},
) {
  async function request(path, init, { timeoutMs } = {}) {
    // таймаут — свой AbortController: AbortSignal.timeout нет в Safari < 16
    const ctl = timeoutMs ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
    const connectionError = () =>
      Object.assign(new Error(NO_CONNECTION), {
        code: ctl?.signal.aborted ? "timeout" : "network",
      });
    try {
      let res;
      try {
        res = await fetchImpl(`${apiBase}${path}`, ctl ? { ...init, signal: ctl.signal } : init);
      } catch {
        throw connectionError();
      }
      if (res.status === 429) {
        // лимит броней (T34) приходит со своим текстом; лимит запросов в минуту — общий текст
        let body = null;
        try {
          body = await res.json();
        } catch {
          /* не-JSON тело */
        }
        const msg =
          body?.code === "limit" && body.error
            ? body.error
            : "Слишком много запросов — подождите минуту.";
        throw Object.assign(new Error(msg), { status: 429, body });
      }
      if (!res.ok) {
        let msg = "Сервис бронирования временно недоступен, попробуйте позже.";
        let body = null;
        try {
          body = await res.json();
          if (body?.error) msg = body.error;
        } catch {
          /* не-JSON тело — оставляем общий текст */
        }
        const err = new Error(msg);
        err.status = res.status;
        err.body = body; // тело 409 несёт code/total/prepaymentRub — нужно экрану оформления
        throw err;
      }
      try {
        return await res.json(); // именно await: иначе finally снимет таймер до чтения тела
      } catch (e) {
        if (ctl?.signal.aborted) throw connectionError();
        throw e;
      }
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  const getJson = (path, opts) => request(path, undefined, opts);
  const postJson = (path, body, opts) =>
    request(
      path,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
      opts,
    );

  return {
    getConfig: () => getJson("/config"),
    getMap: () => getJson("/map"),
    // bg=1 (T23c): пачка цен длин — «фон гостя», после quote и брони в очереди сервера
    getLengths: (from, guests) => getJson(`/lengths?from=${from}&guests=${guests}&bg=1`),
    // цена одного заезда для карточек «Найти» (T23c), ответ в формате /lengths с одной длиной;
    // единственный запрос с таймаутом (T24) — повтор делает lib/search.js
    getQuote: ({ from, to, adults, children }) =>
      getJson(`/quote?from=${from}&to=${to}&adults=${adults}&children=${children ?? 0}`, {
        timeoutMs: quoteTimeoutMs,
      }),
    getCheckouts: (from, guests) => getJson(`/checkouts?from=${from}&guests=${guests}`),
    getPaymentOptions: ({ from, to, adults, children, catId, tariffId }) =>
      getJson(
        `/payment-options?from=${from}&to=${to}&adults=${adults}&children=${children ?? 0}` +
          `&catId=${catId}&tariffId=${tariffId}`,
      ),
    // доп. услуги (T11): тот же набор параметров, что у способов оплаты
    getServices: ({ from, to, adults, children, catId, tariffId }) =>
      getJson(
        `/services?from=${from}&to=${to}&adults=${adults}&children=${children ?? 0}` +
          `&catId=${catId}&tariffId=${tariffId}`,
      ),
    getOrder: (orderToken) => getJson(`/order?orderToken=${encodeURIComponent(orderToken)}`),
    // таймаут и у брони (T34): повтор с тем же attemptKey второй брони не создаст
    book: (payload) => postJson("/book", payload, { timeoutMs: bookTimeoutMs }),
    // события календаря (T37) — в лог сервера; ответ не нужен, ошибки глушим: аналитика не
    // должна мешать гостю. keepalive — запрос долетит, даже если гость уходит со страницы
    calEvent(params) {
      const q = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]));
      try {
        Promise.resolve(fetchImpl(`${apiBase}/cal-event?${q}`, { keepalive: true })).catch(
          () => {},
        );
      } catch {
        /* fetch недоступен — событие теряется молча */
      }
    },
  };
}
